// Stride's own calendar as a pop-over — for a date CELL that saves.
//
// WHY NOT <input type="date">: the browser's picker writes a whole date into
// the input the moment the reader changes its month or year selector, and a
// cell that saves on change then saves a date nobody picked. Here moving
// between months is local view state only (see DateEditor); a date is written
// ONLY when a day (or Today / Clear) is clicked. One click on the cell opens
// it, one click on a day saves and closes. Escape or a click anywhere else
// closes it and writes nothing.
//
// Rendered into document.body so a table or card that clips its overflow
// cannot cut it off; placed under the anchor, or above it when there is no
// room below.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import DateEditor from '@modules/database/work-items/frontend/editors/DateEditor';
import '@modules/database/work-items/frontend/editors/FieldEditor.css';

const W = 262;
const H = 330;

// `anchor` = a DOMRect (or anything with left/top/bottom). `value` is
// 'YYYY-MM-DD' or ''. `onPick(iso | null)` — null means Clear.
export default function DatePopover({ anchor, value, onPick, onClose, disabled = false, zIndex = 4200 }) {
  const box = useRef(null);
  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    const down = (e) => { if (box.current && !box.current.contains(e.target)) onClose(); };
    window.addEventListener('keydown', key, true);
    // Next tick, so the click that opened it does not close it.
    const t = setTimeout(() => window.addEventListener('mousedown', down, true), 0);
    return () => { clearTimeout(t); window.removeEventListener('keydown', key, true); window.removeEventListener('mousedown', down, true); };
  }, [onClose]);
  if (!anchor || typeof document === 'undefined') return null;
  const vw = window.innerWidth; const vh = window.innerHeight;
  const left = Math.max(8, Math.min(anchor.left, vw - W - 8));
  const below = anchor.bottom + 4;
  const top = below + H > vh - 8 && anchor.top - H - 4 > 8 ? anchor.top - H - 4 : Math.max(8, Math.min(below, vh - H - 8));
  const iso = /^\d{4}-\d{2}-\d{2}/.test(String(value || '')) ? String(value).slice(0, 10) : '';
  return createPortal(
    <div ref={box} role="dialog" aria-label="Pick a date" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}
      style={{ position: 'fixed', left, top, width: W, zIndex, background: 'var(--bg-primary, #fff)', border: '1px solid var(--border, #dfe1e6)', borderRadius: 10, boxShadow: '0 12px 32px rgba(0,0,0,.22)', padding: '6px 6px 4px' }}>
      <DateEditor value={iso} disabled={disabled} onAutoSave={(v) => { onPick(v || null); onClose(); }} />
    </div>,
    document.body,
  );
}
